/**
 * Cliente del API del catálogo.
 *
 * Autenticación: NO hay tokens acá. Cloudflare Access ya protege /admin y
 * /api/admin/*; el navegador lleva la cookie `CF_Authorization` sola porque el API
 * vive en el MISMO hostname que el panel (por eso se descartó api.odyscents.com).
 * De ahí `credentials: 'same-origin'`.
 *
 * Si la sesión de Access expira (24 h), la respuesta es un redirect al login. Un
 * fetch no puede seguirlo de forma útil, así que lo detectamos y recargamos: el
 * navegador hace el flujo de login y vuelve.
 */

const base = '/api';

async function pedir(ruta, opciones = {}) {
  const res = await fetch(`${base}${ruta}`, {
    credentials: 'same-origin',
    redirect: 'follow',
    ...opciones,
  });

  // Sesión de Access vencida: la respuesta viene del login, no de nuestro API.
  const tipo = res.headers.get('content-type') || '';
  if (!tipo.includes('application/json')) {
    if (res.redirected || res.status === 302) {
      window.location.reload();
      throw new Error('sesión expirada');
    }
    throw new Error('El servidor respondió algo inesperado. Recargá la página.');
  }

  const datos = await res.json();
  if (!res.ok) {
    const detalle = Array.isArray(datos?.errores) ? datos.errores.join('. ') : null;
    throw new Error(detalle || mensajeDeError(datos?.error, res.status));
  }
  return datos;
}

function mensajeDeError(codigo, status) {
  switch (codigo) {
    case 'duplicado':
      return 'Ya existe un producto con ese nombre y esa marca.';
    case 'no_encontrado':
      return 'Ese producto ya no existe. Recargá la lista.';
    case 'validacion':
      return 'Revisá los datos: hay algo incompleto.';
    case 'muy_grande':
      return 'La foto pesa demasiado.';
    case 'no_parece_imagen':
    case 'tipo_no_soportado':
    case 'tipo_no_coincide':
      return 'Ese archivo no es una foto válida.';
    case 'unauthorized':
      return 'Tu sesión venció. Recargá la página para volver a entrar.';
    case 'db_error':
    case 'r2_error':
      return 'Hubo un problema del lado del servidor. Probá de nuevo en un momento.';
    default:
      return `No se pudo completar la operación (código ${status}).`;
  }
}

export const listar = () => pedir('/admin/products');

export const crear = (producto) =>
  pedir('/admin/products', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(producto),
  });

export const editar = (id, cambios) =>
  pedir(`/admin/products/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(cambios),
  });

export const ocultar = (id) =>
  pedir(`/admin/products/${encodeURIComponent(id)}`, { method: 'DELETE' });

/** `items` = solo los productos que cambiaron de lugar: [{id, position}]. */
export const reordenar = (items) =>
  pedir('/admin/reorder', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items }),
  });

export const subirImagen = (blob) =>
  pedir('/admin/uploads', {
    method: 'POST',
    headers: { 'Content-Type': blob.type },
    body: blob,
  });

export const leerAjustes = () => pedir('/admin/settings');

/** Parcial a propósito: se manda sólo lo que la dueña tocó, así un interruptor
 *  nunca pisa el otro si dos pestañas están abiertas. */
export const guardarAjustes = (cambios) =>
  pedir('/admin/settings', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(cambios),
  });
